import { Preferences } from "@capacitor/preferences";
import { computed, ref } from "vue";

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24h
const WARNING_THRESHOLD_MS = 60 * 60 * 1000; // 1h
const POPUP_THRESHOLD_MS = 15 * 60 * 1000; // 15 min
const STORAGE_KEY = "session_expires_at";

export const sessionExpiresAt = ref<number | null>(null);
export const sessionRemainingMs = ref(0);
export const sessionPopupOpen = ref(false);

let hasShownPopup = false;
let timerId: number | null = null;
let hasLoadedFromStorage = false;

const readStoredExpiration = async () => {
  const { value } = await Preferences.get({ key: STORAGE_KEY });
  if (value) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      sessionExpiresAt.value = parsed;
    }
  }
  hasLoadedFromStorage = true;
};

const persistExpiration = async () => {
  if (sessionExpiresAt.value) {
    await Preferences.set({
      key: STORAGE_KEY,
      value: sessionExpiresAt.value.toString(),
    });
  }
};

const clearPersistedExpiration = async () => {
  await Preferences.remove({ key: STORAGE_KEY });
};

const updateRemainingTime = () => {
  if (!sessionExpiresAt.value) {
    sessionRemainingMs.value = 0;
    return;
  }

  const remaining = sessionExpiresAt.value - Date.now();
  sessionRemainingMs.value = Math.max(0, remaining);

  if (remaining <= 0) {
    sessionPopupOpen.value = false;
    return;
  }

  if (remaining <= POPUP_THRESHOLD_MS && !hasShownPopup) {
    sessionPopupOpen.value = true;
    hasShownPopup = true;
  }
};

const startTicker = () => {
  if (timerId) return;
  updateRemainingTime();
  timerId = window.setInterval(updateRemainingTime, 30000); // every 30s
};

const stopTicker = () => {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
};

export const initSessionTimer = async () => {
  if (!hasLoadedFromStorage) {
    await readStoredExpiration();
  }
  if (sessionExpiresAt.value) {
    startTicker();
  }
};

export const markSessionStart = async (startDate = Date.now()) => {
  sessionExpiresAt.value = startDate + SESSION_DURATION_MS;
  hasShownPopup = false;
  sessionPopupOpen.value = false;
  await persistExpiration();
  startTicker();
};

export const resetSessionTimer = async () => {
  sessionExpiresAt.value = null;
  sessionRemainingMs.value = 0;
  sessionPopupOpen.value = false;
  hasShownPopup = false;
  stopTicker();
  await clearPersistedExpiration();
};

export const forceOpenSessionPopup = () => {
  if (!sessionPopupOpen.value) {
    sessionPopupOpen.value = true;
  }
};

const formatDuration = (milliseconds: number) => {
  const totalMinutes = Math.max(0, Math.floor(milliseconds / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, "0")}min`;
  return `${minutes}min`;
};

export const sessionProgress = computed(() => {
  if (!sessionExpiresAt.value) return 0;
  const raw = (sessionRemainingMs.value / SESSION_DURATION_MS) * 100;
  return Math.min(100, Math.max(0, raw));
});

export const sessionState = computed<"idle" | "ok" | "warning" | "critical">(
  () => {
    if (!sessionExpiresAt.value || sessionRemainingMs.value <= 0) return "idle";
    if (sessionRemainingMs.value <= POPUP_THRESHOLD_MS) return "critical";
    if (sessionRemainingMs.value <= WARNING_THRESHOLD_MS) return "warning";
    return "ok";
  }
);

export const shouldDisplaySessionIndicator = computed(() => {
  // Show the indicator only when we are approaching the end of the 24h window.
  return (
    !!sessionExpiresAt.value &&
    sessionRemainingMs.value > 0 &&
    sessionRemainingMs.value <= WARNING_THRESHOLD_MS
  );
});

export const sessionRemainingLabel = computed(() => {
  if (!sessionExpiresAt.value) return "Session inconnue";
  if (sessionRemainingMs.value <= 0) return "Session expirée";
  return formatDuration(sessionRemainingMs.value);
});
